/**
 * Frontend feature flags — read-only, evaluated client-side.
 *
 * Resolution order per flag:
 *   1. localStorage override (`xpert:flags:<flag>` = "on" | "off")
 *      → developer + QA tool, no UI for it
 *   2. URL query override (`?flag.<flag>=on`)
 *      → preview links, no UI for it
 *   3. Compile-time default below
 *
 * Why client-side and not server-driven:
 *   Xpert doesn't have a feature-flag service yet (no LaunchDarkly, etc.).
 *   This helper is a stand-in so spec-required flags work right now and the
 *   call sites are stable. When a real flag service lands we swap the
 *   resolution body without touching callers.
 */

const FLAG_DEFAULTS = {
  // BR-checkout-ack — page-load auth gate + state machine + 401-safe UX
  // for the booking confidentiality acknowledgement screen. Default ON
  // because the OLD path is the bug (leaks "Not authenticated" to DOM).
  // QA + emergency rollback: localStorage.setItem('xpert:flags:checkout_ack_auth_gate_v2','off')
  checkout_ack_auth_gate_v2: true,

  // Discovery v2 — landing + experts + profile rebuild (2026-05-26 session).
  //   • LandingPage emits telemetry on hero/tier/featured-expert/usecase CTAs
  //     and reads curated examples from src/data/landingCurated.js (which is
  //     explicitly labelled curated, not "live recent reviews from the
  //     platform" — the previous copy violated CLAUDE.md's honesty rule).
  //   • ExpertsPage adds a distinct error state (was conflated with the
  //     empty-search state), retry, aria-busy/aria-live, and result-loaded
  //     telemetry. Migrated to the central axios `api` client.
  //   • ExpertProfilePage replaces the full-page spinner with a skeleton,
  //     deletes the dead handleBooking handler, adds a trust-signal rail
  //     (verified-by-Xpert, typical response, refund), and emits
  //     profile_loaded + profile_book_clicked telemetry.
  // Default ON. Rollback path:
  //   localStorage.setItem('xpert:flags:discovery_v2','off')
  // (Note: turning the flag off does NOT revert the data extraction or
  // the dead-code deletion — only the telemetry emission and the
  // skeleton/error-state UI fall back to legacy behaviour.)
  discovery_v2: true,

  // Get Verified v2 — funnel friction reduction (2026-05-26 session).
  //   • Persistent "Already have an account? Sign in →" link visible in
  //     the wizard header on every step. Routes returning users to
  //     /login?return_to=/get-verified so they sign in early and the
  //     existing auto-skip past STEP.ACCOUNT (line ~232 of
  //     GetVerifiedPage.jsx) fires without them ever seeing step 5.
  //   • StepAccount leads with one-click "Continue with Google"
  //     (the OAuth path already exists at /api/auth/google — same
  //     pattern LoginPage uses). Email + password form drops below an
  //     "or" divider with reduced visual weight.
  //   • Telemetry on wizard lifecycle (started / step advanced / back /
  //     account-method-chosen / submitted / resumed-from-draft /
  //     signin-link-clicked) so we can finally measure where the funnel
  //     bleeds users.
  // Default ON. Rollback path:
  //   localStorage.setItem('xpert:flags:getverified_v2','off')
  // (Telemetry stops emitting; the visible SignInLink and Google button
  // remain — they're UX improvements, not behaviour toggles, so the flag
  // only gates the *measurement* layer when turned off.)
  getverified_v2: true,

  // Session review v1 — the post-session feedback flow at
  // `/session/:bookingId/review`. Closes the marketplace flywheel: live
  // sessions previously ended in silence (no rating capture, no expert
  // notification of buyer satisfaction). Async reviews already had this
  // (`VerificationDetailPage`); live sessions didn't.
  //
  // Default ON because the page is greenfield — turning the flag off
  // means routes still resolve and tests still pass, but the dashboard
  // card linking to the page is hidden and telemetry emission is gated.
  // Emergency rollback:
  //   localStorage.setItem('xpert:flags:session_review_v1','off')
  session_review_v1: true,
};

function readLocalStorage(name) {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(`xpert:flags:${name}`);
  } catch {
    return null;
  }
}

function readQuery(name) {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(`flag.${name}`);
  } catch {
    return null;
  }
}

function parseBool(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).toLowerCase();
  if (s === "on" || s === "true" || s === "1") return true;
  if (s === "off" || s === "false" || s === "0") return false;
  return null;
}

/**
 * Read a feature flag. Returns boolean.
 *
 * @param {keyof typeof FLAG_DEFAULTS} name
 */
export function isFlagOn(name) {
  const ls = parseBool(readLocalStorage(name));
  if (ls !== null) return ls;
  const q = parseBool(readQuery(name));
  if (q !== null) return q;
  return !!FLAG_DEFAULTS[name];
}

/** Exposed for tests + a future devtools panel. */
export const __flags = { FLAG_DEFAULTS };
